import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSendTo } from '../../hooks/useSendTo';
import { SendDestination } from '../../types/sendTo';
import { FiSend, FiPackage, FiFileText, FiGitBranch, FiUserPlus } from 'react-icons/fi';
import { usePersonaChatUI } from '../../contexts/PersonaChatUIContext';

interface MessageActionsProps {
  messageContent: string;
  sessionId: string;
  personaId: string;
  personaName: string;
  /** C2.1 sp4 — the turn id; enables the "invite a peer onto the trail" action. */
  messageId?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  messageContent,
  sessionId,
  personaId,
  personaName,
  messageId,
}) => {
  const { darkMode } = useTheme();
  const { openInvitePeer, canInvitePeer } = usePersonaChatUI();
  const { sendToDestination, isSending } = useSendTo();
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSend = async (destination: SendDestination) => {
    setShowPopover(false);
    await sendToDestination(messageContent, destination, {
      sessionId,
      personaId,
      personaName,
      sendMode: 'single_message',
      messageCount: 1
    });
  };

  return (
    <div className="relative flex items-center gap-0.5" ref={popoverRef}>
      {canInvitePeer && messageId && (
        <button
          type="button"
          data-testid="invite-peer-action"
          onClick={() => openInvitePeer(messageId)}
          className={`p-1 rounded transition-colors ${
            darkMode
              ? 'hover:bg-gray-600 text-gray-400 hover:text-teal-300'
              : 'hover:bg-gray-200 text-gray-400 hover:text-teal-600'
          }`}
          title="Invite a peer onto the trail at this turn"
        >
          <FiUserPlus className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={() => setShowPopover(!showPopover)}
        disabled={isSending}
        className={`p-1 rounded transition-colors ${
          darkMode
            ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
            : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
        } ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="Send this message to..."
      >
        <FiSend className="w-3.5 h-3.5" />
      </button>

      {showPopover && (
        <div className={`absolute right-0 bottom-full mb-1 w-48 rounded-md shadow-lg z-30 ${
          darkMode ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-200'
        }`}>
          <div className="py-1">
            <div className={`px-3 py-1 text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Send to:
            </div>
            <button
              onClick={() => handleSend('artifactory_project')}
              className={`w-full px-3 py-1.5 text-xs text-left flex items-center ${
                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiPackage className="w-3.5 h-3.5 mr-2 text-purple-500" />
              Artifactory Project
            </button>
            <button
              onClick={() => handleSend('artifact')}
              className={`w-full px-3 py-1.5 text-xs text-left flex items-center ${
                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiFileText className="w-3.5 h-3.5 mr-2 text-green-500" />
              Artifact
            </button>
            <button
              disabled
              className={`w-full px-3 py-1.5 text-xs text-left flex items-center opacity-40 cursor-not-allowed ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
              title="Coming soon"
            >
              <FiGitBranch className="w-3.5 h-3.5 mr-2 text-orange-500" />
              Workflow
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
